// Ultimate Card Game (UCG) - Custom Deck Persistence
// Player-built decks, saved to localStorage so they survive a reload.

import { CARDS_DATA } from './cards.js';

// Bumped for Neustart "Marine Null": old custom decks reference now-
// disabled cards, so they're not migrated - a fresh key just means
// existing localStorage entries under the old key are silently ignored.
const STORAGE_KEY = 'ucg_customDecks_v2';

export const MAX_CUSTOM_DECKS = 3;

let inMemoryCustomDecks = null;
const deckListeners = new Set();

export function addDeckListener(fn) {
  deckListeners.add(fn);
  return () => deckListeners.delete(fn);
}

function notifyDeckListeners(decks) {
  for (const fn of deckListeners) {
    try { fn(decks); } catch (e) { console.error(e); }
  }
}

export const MIN_DECK_SIZE = 40;
export const MAX_COPIES_PER_CARD = 3;

/**
 * Returns the maximum legal copies of a card allowed in a deck.
 * Base modules (Capsule Sling, Drone Command, Congress Module, etc.) can only be built
 * once per match, so they are strictly limited to 1 copy per deck (Singleton).
 * Units and spells can have up to MAX_COPIES_PER_CARD (3).
 *
 * @param {Object|string} card - Card object (or type string)
 * @returns {number} Maximum allowed copies (1 for Modules, 3 for others)
 */
export function getMaxCopiesForCard(card) {
  if (!card) return MAX_COPIES_PER_CARD;
  if (typeof card === 'string') {
    return card === 'Module' ? 1 : MAX_COPIES_PER_CARD;
  }
  if (card.type === 'Module' || card.isModule) {
    return 1;
  }
  return MAX_COPIES_PER_CARD;
}

/**
 * Ensures a custom deck strictly adheres to per-card copy limits (Singleton for Modules, max 3 for others).
 * Re-synchronizes cards map, common, uncommon, and epic tier arrays.
 *
 * @param {Object} deck - The raw or parsed custom deck object
 * @param {Array} cardsData - List of all card definitions (defaults to CARDS_DATA)
 * @returns {{ deck: Object, changed: boolean }} Sanitized deck and boolean flag
 */
export function sanitizeCustomDeck(deck, cardsData = CARDS_DATA) {
  if (!deck || typeof deck !== 'object') {
    return { deck, changed: false };
  }

  let changed = false;
  const cardsMap = {};

  if (deck.cards && typeof deck.cards === 'object') {
    for (const [name, qty] of Object.entries(deck.cards)) {
      const card = cardsData.find(c => c.name.toLowerCase() === name.toLowerCase());
      const maxAllowed = getMaxCopiesForCard(card);
      const originalCount = Number(qty) || 0;
      const clamped = Math.min(maxAllowed, Math.max(0, originalCount));
      if (clamped !== originalCount) {
        changed = true;
      }
      if (clamped > 0) {
        const canonicalName = card?.name || name;
        cardsMap[canonicalName] = clamped;
      }
    }
  } else {
    const allNames = [...(deck.common || []), ...(deck.uncommon || []), ...(deck.epic || [])];
    const counts = {};
    for (const name of allNames) {
      if (!name) continue;
      counts[name] = (counts[name] || 0) + 1;
    }
    for (const [name, qty] of Object.entries(counts)) {
      const card = cardsData.find(c => c.name.toLowerCase() === name.toLowerCase());
      const maxAllowed = getMaxCopiesForCard(card);
      const clamped = Math.min(maxAllowed, qty);
      if (clamped !== qty) {
        changed = true;
      }
      if (clamped > 0) {
        const canonicalName = card?.name || name;
        cardsMap[canonicalName] = clamped;
      }
    }
  }

  const common = [];
  const uncommon = [];
  const epic = [];
  for (const [cardName, count] of Object.entries(cardsMap)) {
    const cardObj = cardsData.find(c => c.name.toLowerCase() === cardName.toLowerCase());
    const tier = cardObj?.tier || 1;
    for (let i = 0; i < count; i++) {
      if (tier === 1) common.push(cardName);
      else if (tier === 2) uncommon.push(cardName);
      else epic.push(cardName);
    }
  }

  if (
    (deck.common?.length || 0) !== common.length ||
    (deck.uncommon?.length || 0) !== uncommon.length ||
    (deck.epic?.length || 0) !== epic.length
  ) {
    changed = true;
  }

  const sanitized = {
    ...deck,
    cards: cardsMap,
    common,
    uncommon,
    epic
  };

  return { deck: sanitized, changed };
}

export function loadCustomDecks() {
  if (inMemoryCustomDecks !== null) {
    return inMemoryCustomDecks;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          let anyChanged = false;
          const sanitized = parsed.map(d => {
            const res = sanitizeCustomDeck(d, CARDS_DATA);
            if (res.changed) anyChanged = true;
            return res.deck;
          });
          inMemoryCustomDecks = sanitized;
          if (anyChanged) {
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
            } catch {}
          }
          return inMemoryCustomDecks;
        }
      }
    }
  } catch {}
  return inMemoryCustomDecks || [];
}

export function reloadCustomDecks() {
  const previous = inMemoryCustomDecks;
  inMemoryCustomDecks = null;
  const loaded = loadCustomDecks();
  if (loaded.length === 0 && previous && previous.length > 0) {
    inMemoryCustomDecks = previous;
    notifyDeckListeners(previous);
    return previous;
  }
  notifyDeckListeners(loaded);
  return loaded;
}

export function saveCustomDecks(decks) {
  const sanitized = Array.isArray(decks)
    ? decks.map(d => sanitizeCustomDeck(d, CARDS_DATA).deck)
    : [];
  inMemoryCustomDecks = sanitized;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(inMemoryCustomDecks));
    }
  } catch {
    // localStorage unavailable (private browsing, quota) - deck stays in-memory for this session only.
  }
  notifyDeckListeners(inMemoryCustomDecks);
}

export function getCustomDeckById(id) {
  return loadCustomDecks().find(d => d.id === id) || null;
}

// Embeds the deck's dominant race in the id itself (e.g. "custom-sul-xxxx")
// so AvatarRenderer.getRaceFromDeck()'s plain substring match picks the
// right commander portrait without any changes to that module.
export function makeCustomDeckId(race) {
  const raceSlug = (race || 'human').toLowerCase();
  const unique = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  return `custom-${raceSlug}-${unique}`;
}

// Single source of truth for "is this custom deck legal to play" - min 40
// cards (max copies per card: 1 for Modules, 3 for others), at least 1 Tier 1 + 1 Tier 2, and
// (unless it's a pure Sul deck) at least 1 Tier 3. Mirrors
// server/deckValidator.js's MIN_TOTAL_CARDS/tier checks (that copy stays
// server-side on purpose - it also enforces copy limits server-
// authoritatively, see the "Server vertraut niemals..." rule in CLAUDE.md)
// so the client only needs one place to agree with itself:
// js/ui/deckBuilderModal.js's inline "why can't I play this" hint
// (renderValidityHint()) and js/ui/deckBuilder.js's actual match-selection gate
// both call this instead of each keeping their own copy of the tier-count rule.
export function getDeckValidity(cardsMap, cardsData, explicitRace = null) {
  let total = 0, t1 = 0, t2 = 0, t3 = 0;
  let hasSul = false, hasHuman = false;
  let hasDuplicates = false;
  const duplicateCards = [];

  for (const [name, qty] of Object.entries(cardsMap || {})) {
    const card = cardsData.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (!card) continue;
    const maxAllowed = getMaxCopiesForCard(card);
    if (qty > maxAllowed) {
      hasDuplicates = true;
      duplicateCards.push({ name: card.name, qty, maxAllowed, isModule: maxAllowed === 1 });
    }
    total += qty;
    if (card.tier === 1) t1 += qty;
    else if (card.tier === 2) t2 += qty;
    else if (card.tier === 3) t3 += qty;
    if (card.race === 'Sul') hasSul = true;
    else hasHuman = true;
  }

  const isSulDeck = (hasSul && !hasHuman) || (explicitRace === 'Sul' && !hasHuman);
  const isValid = !hasDuplicates && total >= MIN_DECK_SIZE && t1 >= 1 && t2 >= 1 && (isSulDeck || t3 >= 1);
  return { total, t1, t2, t3, isSulDeck, isValid, hasDuplicates, duplicateCards };
}

export function deriveDominantRace(cardNames, cardsData) {
  // Module override rule:
  // If at least one Human module is selected, the deck race is always Human.
  const hasHumanModule = (cardNames || []).some(name => {
    const card = cardsData.find(c => c.name.toLowerCase() === (typeof name === 'string' ? name : name?.name || '').toLowerCase());
    return card && (card.type === 'Module' || card.isModule) && (!card.race || card.race === 'Human');
  });
  if (hasHumanModule) return 'Human';

  const counts = {};
  for (const name of cardNames) {
    const card = cardsData.find(c => c.name.toLowerCase() === (typeof name === 'string' ? name : name?.name || '').toLowerCase());
    const race = card?.race || 'Human';
    counts[race] = (counts[race] || 0) + 1;
  }
  let best = 'Human', bestCount = -1;
  for (const [race, count] of Object.entries(counts)) {
    if (count > bestCount) { best = race; bestCount = count; }
  }
  return best;
}

export const DECK_ICON_PRESETS = Object.freeze([
  { id: 'generic-a', glyph: '◈', label: 'Diamond', isCrest: true, rarity: 'common', surgeReward: false, isStarter: true },
  { id: 'generic-b', glyph: '❖', label: 'Cross', isCrest: true, rarity: 'common', surgeReward: false, isStarter: true },
  { id: 'generic-c', glyph: '▲', label: 'Delta', isCrest: true, rarity: 'common', surgeReward: false, isStarter: true },
  { id: 'generic-d', glyph: '⬡', label: 'Hexagon', isCrest: true, rarity: 'common', surgeReward: false, isStarter: true },
  { id: 'human-crest', glyph: '✦', isCrest: true, race: 'Human', label: 'Human Vanguard Crest', rarity: 'common', surgeReward: false, isStarter: true },
  { id: 'sul-crest', glyph: '✹', isCrest: true, race: 'Sul', label: 'Sul Swarm Crest', rarity: 'common', surgeReward: false, isStarter: true },
  // 7 Unlockable Supply Surge Masterworks
  { id: 'valkyrie-aegis', glyph: '⚔', isCrest: true, race: 'Human', label: 'Valkyrie Aegis', rarity: 'rare', surgeReward: true, faction: 'Human Vanguard' },
  { id: 'void-singularity', glyph: '✺', isCrest: true, label: 'Void Singularity', rarity: 'epic', surgeReward: true, faction: 'Deep Void' },
  { id: 'brood-oculus', glyph: '👁', isCrest: true, race: 'Sul', label: 'Brood Oculus', rarity: 'rare', surgeReward: true, faction: 'Sul Swarm' },
  { id: 'foundry-titan', glyph: '⚙', isCrest: true, label: 'Foundry Titan', rarity: 'rare', surgeReward: true, faction: 'Scrap Syndicate' },
  { id: 'solar-crown', glyph: '☀', isCrest: true, label: 'Solar Crown', rarity: 'epic', surgeReward: true, faction: 'Solar Imperial Guard' },
  { id: 'ghost-protocol', glyph: '☠', isCrest: true, label: 'Ghost Protocol', rarity: 'rare', surgeReward: true, faction: 'Black Ops' },
  { id: 'rejected-crown', glyph: '𓏏', isCrest: true, race: 'Sul', label: 'Rejected Crown', rarity: 'rare', surgeReward: true, faction: 'Sul Loaf Caste' }
]);

